import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  Activity, 
  Search, 
  Filter,
  LogOut,
  Shield,
  Building2,
  DollarSign,
  UserCheck,
  AlertCircle,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  Calendar,
  MoreVertical,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const SUPER_USER_API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_FULL = '/images/logo-full.png';

/** Isolated client so super-user Bearer never pollutes global axios / tenant session. */
const superApi = axios.create({
  baseURL: SUPER_USER_API_URL,
  withCredentials: true,
});
superApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('super_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function SuperUserDashboardPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [billingData, setBillingData] = useState(null);
  const [selectedSubscriberId, setSelectedSubscriberId] = useState(null);
  const [subscriberDetails, setSubscriberDetails] = useState(null);
  const [subscriberDetailsLoading, setSubscriberDetailsLoading] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [adminQrPayload, setAdminQrPayload] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('super_access_token');
    if (!token) {
      navigate('/super-user/login');
    }
  }, [navigate]);

  // Sync tab from URL (?tab=overview|subscribers|billing)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['overview', 'subscribers', 'billing'].includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search, activeTab]);

  const setTabAndUrl = (tab) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate({ pathname: '/super-user/dashboard', search: params.toString() }, { replace: true });
    setActiveTab(tab);
  };

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchMetrics();
    } else if (activeTab === 'subscribers') {
      fetchSubscribers();
    } else if (activeTab === 'billing') {
      fetchBillingMetrics();
    }
  }, [activeTab, currentPage, searchTerm, statusFilter, planFilter, includeArchived]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const [metricsRes, subscribersRes] = await Promise.all([
        superApi.get(`${SUPER_USER_API_URL}/api/billing/metrics`),
        superApi.get(`${SUPER_USER_API_URL}/api/subscribers?limit=5`)
      ]);
      
      setMetrics(metricsRes.data);
      setSubscribers(subscribersRes.data.subscribers);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(includeArchived ? { include_archived: 'true' } : {}),
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status_filter: statusFilter }),
        ...(planFilter && { plan_filter: planFilter })
      });
      
      const response = await superApi.get(`${SUPER_USER_API_URL}/api/subscribers?${params}`);
      setSubscribers(response.data.subscribers);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch subscribers');
      console.error('Subscribers error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingMetrics = async () => {
    try {
      setLoading(true);
      const [metricsRes, plansRes, subscriptionsRes] = await Promise.all([
        superApi.get(`${SUPER_USER_API_URL}/api/billing/metrics`),
        superApi.get(`${SUPER_USER_API_URL}/api/billing/plans`),
        superApi.get(`${SUPER_USER_API_URL}/api/billing/subscriptions?limit=10`)
      ]);
      
      setBillingData({
        metrics: metricsRes.data,
        plans: plansRes.data,
        subscriptions: subscriptionsRes.data.subscriptions
      });
    } catch (error) {
      toast.error('Failed to fetch billing data');
      console.error('Billing error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriberDetails = async (organizationId) => {
    try {
      setSubscriberDetailsLoading(true);
      const res = await superApi.get(`${SUPER_USER_API_URL}/api/subscribers/${organizationId}`);
      setSubscriberDetails(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load subscriber details');
    } finally {
      setSubscriberDetailsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await superApi.post(`${SUPER_USER_API_URL}/api/super-user/logout`);
    } catch (error) {
      // Clear local session even if logout API fails
    }
    localStorage.removeItem('super_access_token');
    navigate('/super-user/login');
    toast.success('Logged out successfully');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-700 bg-green-100';
      case 'suspended': return 'text-red-700 bg-red-100';
      case 'cancelled': return 'text-gray-700 bg-gray-100';
      case 'archived': return 'text-amber-800 bg-amber-100';
      default: return 'text-yellow-700 bg-yellow-100';
    }
  };

  // User management functions
  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
      await superApi.post(`${SUPER_USER_API_URL}/api/subscribers/${userId}/toggle-status`, {
        status: newStatus
      });
      
      toast.success(`User ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      fetchSubscribers(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleExtendTrial = async (userId, days) => {
    try {
      await superApi.post(`${SUPER_USER_API_URL}/api/subscribers/${userId}/extend-trial`, {
        days: days
      });
      
      toast.success(`Trial period extended by ${days} days`);
      fetchSubscribers(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend trial period');
    }
  };

  const handleArchiveSubscriber = async (organizationId) => {
      try {
      await superApi.post(`${SUPER_USER_API_URL}/api/subscribers/${organizationId}/archive`, {
        reason: archiveReason.trim() || null,
      });
      toast.success('Company archived. Data retained for audit; tenant login is disabled.');
      setShowArchiveConfirm(false);
      setArchiveReason('');
      setSelectedSubscriberId(null);
      setSubscriberDetails(null);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive company');
    }
  };

  const handlePermanentDelete = async (organizationId) => {
    const expected = (
      subscriberDetails?.organization?.name ||
      selectedSubscriberSummary?.organization_name ||
      ''
    ).trim();
    if (!expected) {
      toast.error('Company name missing — cannot confirm delete');
      return;
    }
    if (deleteConfirmName.trim().toLowerCase() !== expected.toLowerCase()) {
      toast.error('Type the exact company name to confirm permanent delete');
      return;
    }
    setDeletingCompany(true);
    try {
      await superApi.delete(
        `${SUPER_USER_API_URL}/api/subscribers/${organizationId}?confirm=true`,
        { data: { confirm_name: deleteConfirmName.trim() } },
      );
      toast.success('Company permanently deleted from the database');
      setShowDeleteConfirm(false);
      setDeleteConfirmName('');
      setSelectedSubscriberId(null);
      setSubscriberDetails(null);
      fetchSubscribers();
      if (activeTab === 'overview') fetchMetrics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete company');
    } finally {
      setDeletingCompany(false);
    }
  };

  const handleIssueAdminQr = async (organizationId) => {
    try {
      const res = await superApi.post(
        `${SUPER_USER_API_URL}/api/subscribers/${organizationId}/issue-admin-bootstrap-qr`,
      );
      setAdminQrPayload(res.data?.admin_mobile_onboarding || null);
      toast.success('Admin mobile QR issued. Show this to the company admin once.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not issue admin QR');
    }
  };

  const selectedSubscriberSummary = useMemo(() => {
    if (!selectedSubscriberId) return null;
    return subscribers.find(s => s.id === selectedSubscriberId) || null;
  }, [subscribers, selectedSubscriberId]);

  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [trialExtensionDays, setTrialExtensionDays] = useState(7);

  const openExtendTrialModal = (user) => {
    setSelectedUser(user);
    setShowExtendTrialModal(true);
    setTrialExtensionDays(7);
  };

  const confirmExtendTrial = () => {
    if (selectedUser) {
      handleExtendTrial(selectedUser.id, trialExtensionDays);
      setShowExtendTrialModal(false);
      setSelectedUser(null);
    }
  };

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img
                src={LOGO_FULL}
                alt="OMNICHEK"
                className="h-8 object-contain"
              />
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-brand-600" />
                <h1 className="text-xl font-semibold text-zinc-900">Super User Portal</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-zinc-600 hover:text-zinc-900"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation tabs */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'subscribers', label: 'Subscribers', icon: Users },
              { id: 'billing', label: 'Billing', icon: CreditCard }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setTabAndUrl(tab.id)}
                className={`flex items-center gap-2 px-1 py-4 border-b-2 font-medium text-sm transition-all ${
                  activeTab === tab.id
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && metrics && (
          <div className="space-y-6">
            {/* Metrics cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <button type="button" className="text-left" onClick={() => setTabAndUrl('subscribers')}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Total Subscribers</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{metrics.total_subscribers}</p>
                    </div>
                    <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-brand-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </button>

              <button type="button" className="text-left" onClick={() => setTabAndUrl('subscribers')}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Active Subscribers</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{metrics.active_subscribers}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </button>

              <button type="button" className="text-left" onClick={() => setTabAndUrl('billing')}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Monthly Revenue</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{formatCurrency(metrics.monthly_revenue)}</p>
                    </div>
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </button>

              <button type="button" className="text-left" onClick={() => setTabAndUrl('billing')}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Churn Rate</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{metrics.churn_rate}%</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </button>
            </div>

            {/* Recent subscribers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-zinc-900">Recent Subscribers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {subscribers.map((subscriber) => (
                  <div key={subscriber.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-zinc-400" />
                      <div>
                        <p className="text-zinc-900 font-medium">{subscriber.organization_name}</p>
                        <p className="text-zinc-500 text-sm">
                          {subscriber.email || '(no user linked)'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-900 text-sm">{subscriber.plan_type}</p>
                      <p className="text-zinc-500 text-xs">{formatDate(subscriber.created_at)}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        type="text"
                        placeholder="Search subscribers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-4 py-2 bg-white border border-zinc-300 rounded-lg text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <select
                      value={planFilter}
                      onChange={(e) => setPlanFilter(e.target.value)}
                      className="px-4 py-2 bg-white border border-zinc-300 rounded-lg text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">All Plans</option>
                      <option value="Free">Free trial</option>
                      <option value="Enterprise">Enterprise</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm text-zinc-700 whitespace-nowrap px-2">
                      <input
                        type="checkbox"
                        checked={includeArchived}
                        onChange={(e) => {
                          setIncludeArchived(e.target.checked);
                          setCurrentPage(1);
                        }}
                        className="rounded border-zinc-300"
                      />
                      Show archived
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscribers list */}
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">QR Codes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Scans</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Trial Ends</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {subscribers.map((subscriber) => (
                      <tr
                        key={subscriber.id}
                        className={`hover:bg-zinc-50 transition-colors cursor-pointer ${
                          selectedSubscriberId === subscriber.id ? 'bg-brand-50' : ''
                        }`}
                        onClick={() => {
                          setSelectedSubscriberId(subscriber.id);
                          fetchSubscriberDetails(subscriber.id);
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 text-zinc-400 mr-2" />
                            <span className="text-zinc-900">{subscriber.organization_name}</span>
                            {subscriber.is_archived ? (
                              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-900">
                                Archived
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-zinc-600">{subscriber.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-zinc-600">{subscriber.plan_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(subscriber.status)}`}>
                            {subscriber.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-zinc-600">{subscriber.qr_codes_generated}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-zinc-600">{subscriber.total_scans}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-zinc-600">
                          {subscriber.trial_ends_at ? formatDate(subscriber.trial_ends_at) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            {/* Enable/Disable Toggle */}
                            <button
                              onClick={() => handleToggleUserStatus(subscriber.id, subscriber.status)}
                              className={`p-2 rounded-lg transition-colors ${
                                subscriber.status === 'active' 
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                  : 'bg-red-100 text-red-600 hover:bg-red-200'
                              }`}
                              title={subscriber.status === 'active' ? 'Disable user' : 'Enable user'}
                            >
                              {subscriber.status === 'active' ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            </button>
                            
                            {/* Extend Trial Button */}
                            <button
                              onClick={() => openExtendTrialModal(subscriber)}
                              className="p-2 bg-brand-100 text-brand-600 rounded-lg hover:bg-brand-200 transition-colors"
                              title="Extend trial period"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                            
                            {/* More Actions */}
                            <div className="relative">
                              <button
                                className="p-2 bg-zinc-100 text-zinc-600 rounded-lg hover:bg-zinc-200 transition-colors"
                                onClick={() => {
                                  setSelectedSubscriberId(subscriber.id);
                                  fetchSubscriberDetails(subscriber.id);
                                  setShowArchiveConfirm(true);
                                }}
                                title="Archive company"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-between">
                  <div className="text-sm text-zinc-500">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setCurrentPage(pagination.page - 1)}
                      disabled={!pagination.has_prev}
                      variant="outline"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <span className="px-3 py-1 text-zinc-600 text-sm">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <Button
                      onClick={() => setCurrentPage(pagination.page + 1)}
                      disabled={!pagination.has_next}
                      variant="outline"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && billingData && (
          <div className="space-y-6">
            {/* Billing metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Total Revenue</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{formatCurrency(billingData.metrics.total_revenue)}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Monthly Revenue</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{formatCurrency(billingData.metrics.monthly_revenue)}</p>
                    </div>
                    <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-brand-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Avg Revenue/User</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{formatCurrency(billingData.metrics.average_revenue_per_user)}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Churn Rate</p>
                      <p className="text-2xl font-bold text-zinc-900 mt-1">{billingData.metrics.churn_rate}%</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent subscriptions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-zinc-900">Recent Subscriptions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {billingData.subscriptions.map((subscription) => (
                  <div key={subscription.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                    <div>
                      <p className="text-zinc-900 font-medium">{subscription.organization_name}</p>
                      <p className="text-zinc-500 text-sm">{subscription.plan_name} - {subscription.billing_cycle}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-900 font-medium">{formatCurrency(subscription.amount)}</p>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(subscription.status)}`}>
                        {subscription.status}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Trial Extension Modal */}
      {showExtendTrialModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-zinc-900">Extend Trial Period</h3>
              <button
                onClick={() => setShowExtendTrialModal(false)}
                className="p-1 hover:bg-zinc-100 rounded-lg"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-zinc-600 mb-1">User</p>
                <p className="font-medium text-zinc-900">{selectedUser.organization_name}</p>
                <p className="text-sm text-zinc-500">{selectedUser.email}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Extend trial by (days)
                </label>
                <select
                  value={trialExtensionDays}
                  onChange={(e) => setTrialExtensionDays(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowExtendTrialModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmExtendTrial}
                  className="flex-1"
                >
                  Extend Trial
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscriber details drawer */}
      {activeTab === 'subscribers' && selectedSubscriberId && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              setSelectedSubscriberId(null);
              setSubscriberDetails(null);
              setShowDeleteConfirm(false);
            }}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl border-l border-zinc-200 overflow-y-auto">
            <div className="p-6 border-b border-zinc-200 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs tracking-widest uppercase text-zinc-400">Subscriber</p>
                <h2 className="text-xl font-semibold text-zinc-900">
                  {selectedSubscriberSummary?.organization_name || '—'}
                </h2>
                <p className="text-sm text-zinc-500">{selectedSubscriberSummary?.email || ''}</p>
              </div>
              <button
                className="p-2 rounded-lg hover:bg-zinc-100"
                onClick={() => {
                  setSelectedSubscriberId(null);
                  setSubscriberDetails(null);
                  setShowDeleteConfirm(false);
                }}
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {subscriberDetailsLoading && (
                <div className="flex items-center gap-3 text-zinc-600">
                  <div className="animate-spin w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full" />
                  Loading details…
                </div>
              )}

              {!subscriberDetailsLoading && subscriberDetails && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg border border-zinc-200 bg-zinc-50">
                      <p className="text-xs text-zinc-500">Plan</p>
                      <p className="text-sm font-semibold text-zinc-900">{selectedSubscriberSummary?.plan_type || '—'}</p>
                    </div>
                    <div className="p-4 rounded-lg border border-zinc-200 bg-zinc-50">
                      <p className="text-xs text-zinc-500">Status</p>
                      <p className="text-sm font-semibold text-zinc-900">{selectedSubscriberSummary?.status || '—'}</p>
                    </div>
                    <div className="p-4 rounded-lg border border-zinc-200 bg-zinc-50">
                      <p className="text-xs text-zinc-500">QR codes</p>
                      <p className="text-sm font-semibold text-zinc-900">{subscriberDetails.metrics?.qr_codes_generated ?? '—'}</p>
                    </div>
                    <div className="p-4 rounded-lg border border-zinc-200 bg-zinc-50">
                      <p className="text-xs text-zinc-500">Total scans</p>
                      <p className="text-sm font-semibold text-zinc-900">{subscriberDetails.metrics?.total_scans ?? '—'}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleToggleUserStatus(selectedSubscriberId, selectedSubscriberSummary?.status)}
                    >
                      {selectedSubscriberSummary?.status === 'active' ? 'Suspend' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => openExtendTrialModal(selectedSubscriberSummary)}
                    >
                      Extend trial
                    </Button>
                    <Button
                      variant="outline"
                      className="border-amber-600 text-amber-900 hover:bg-amber-50"
                      onClick={() => setShowArchiveConfirm(true)}
                      disabled={subscriberDetails?.organization?.is_archived}
                    >
                      Unsubscribe / Archive
                    </Button>
                    <Button
                      variant="outline"
                      className="border-red-600 text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setDeleteConfirmName('');
                        setShowDeleteConfirm(true);
                      }}
                    >
                      Delete permanently
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => void handleIssueAdminQr(selectedSubscriberId)}
                      disabled={subscriberDetails?.organization?.is_archived}
                    >
                      Issue admin mobile QR
                    </Button>
                  </div>

                  <div className="rounded-lg border border-zinc-200">
                    <div className="p-4 border-b border-zinc-200">
                      <p className="text-sm font-semibold text-zinc-900">Users</p>
                    </div>
                    <div className="p-4 space-y-2">
                      {(subscriberDetails.users || []).slice(0, 10).map(u => (
                        <div key={u._id || u.id} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-zinc-900">{u.email}</p>
                            <p className="text-xs text-zinc-500">{u.role}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {u.is_active ? 'active' : 'disabled'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archive confirmation — soft-offboard; data kept for audit */}
      {showArchiveConfirm && selectedSubscriberId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-zinc-900">Unsubscribe / Archive company</h3>
              <button
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setArchiveReason('');
                }}
                className="p-1 hover:bg-zinc-100 rounded-lg"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>
            <p className="text-sm text-zinc-600 mb-3">
              Disables tenant login and marks the subscription archived. Organizations, users, QRs, and logs remain in the
              database for audits.
            </p>
            <label className="block text-xs font-medium text-zinc-700 mb-1">Reason (optional)</label>
            <Input
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="e.g. Customer request, contract ended"
              className="mb-4"
            />
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setArchiveReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={() => void handleArchiveSubscriber(selectedSubscriberId)}
              >
                Archive
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent delete — irreversible cascade purge */}
      {showDeleteConfirm && selectedSubscriberId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-700">Delete company permanently</h3>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmName('');
                }}
                className="p-1 hover:bg-zinc-100 rounded-lg"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>
            <p className="text-sm text-zinc-700 mb-2">
              This permanently removes the company and all related data from the database (users, products, QRs, scans,
              subscriptions, roles, printers, logs, keys, uploads). This cannot be undone.
            </p>
            <p className="text-sm text-zinc-600 mb-3">
              Type the company name exactly to confirm:{' '}
              <span className="font-semibold text-zinc-900">
                {subscriberDetails?.organization?.name || selectedSubscriberSummary?.organization_name || '—'}
              </span>
            </p>
            <Input
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder="Company name"
              className="mb-4"
              autoFocus
            />
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                disabled={deletingCompany}
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmName('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={deletingCompany}
                onClick={() => void handlePermanentDelete(selectedSubscriberId)}
              >
                {deletingCompany ? 'Deleting…' : 'Delete forever'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {adminQrPayload?.qr_image_base64 ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-zinc-900">Admin mobile QR</h3>
              <button type="button" onClick={() => setAdminQrPayload(null)} className="p-1 hover:bg-zinc-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Single-use ABOOT code. Admin scans in the app under Scan login QR.</p>
            <div className="flex justify-center p-4 bg-zinc-50 rounded-lg">
              <img
                src={`data:image/png;base64,${adminQrPayload.qr_image_base64}`}
                alt="Admin QR"
                className="w-56 h-56"
              />
            </div>
            <Button variant="outline" className="w-full mt-4" onClick={() => setAdminQrPayload(null)}>
              Close
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default SuperUserDashboardPage;
